/**
 * Bailey Service — admin AI chat client.
 *
 * Streams responses from /api/v2/admin/bailey/chat/stream as SSE and
 * dispatches event objects to the caller. Also exposes session list
 * + transcript fetch for the session sidebar.
 */

import siteSetting from '../config/env';

const apiV2Url = siteSetting.api.v2;

function authHeader() {
  const token = localStorage.getItem('accessToken');
  return {
    'Content-Type': 'application/json',
    accessToken: token || ''
  };
}

async function handleJson(response) {
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.statusCode === 0) {
    const message = (data && data.message) || response.statusText || 'Request failed';
    throw new Error(message);
  }
  return data;
}

/**
 * Stream a chat response. Calls onEvent for every SSE frame parsed.
 * Resolves when the stream finishes; throws if anything errors.
 *
 * @param {Object} params { sessionId?, message }
 * @param {(event: Object) => void} onEvent
 * @param {AbortSignal} [signal] — pass to abort the stream mid-flight
 */
export async function streamChat(params, onEvent, signal) {
  const response = await fetch(`${apiV2Url}admin/bailey/chat/stream`, {
    method: 'POST',
    headers: authHeader(),
    body: JSON.stringify(params || {}),
    signal
  });

  if (!response.ok) {
    let message;
    try {
      const j = await response.json();
      message = j.message;
    } catch (err) {
      message = response.statusText;
    }
    throw new Error(message || `HTTP ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE frames are separated by a blank line
    let nl;
    while ((nl = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, nl);
      buffer = buffer.slice(nl + 2);
      if (!frame.startsWith('data: ')) continue;
      const payload = frame.slice(6);
      if (payload === '[DONE]') return;
      try {
        onEvent(JSON.parse(payload));
      } catch (err) {
        // Skip malformed frames silently
      }
    }
  }
}

export async function listSessions() {
  const res = await fetch(`${apiV2Url}admin/bailey/sessions`, {
    method: 'GET',
    headers: authHeader()
  });
  const data = await handleJson(res);
  return data.data || [];
}

export async function getSessionTranscript(sessionId) {
  const res = await fetch(`${apiV2Url}admin/bailey/sessions/${sessionId}`, {
    method: 'GET',
    headers: authHeader()
  });
  const data = await handleJson(res);
  return data.data || [];
}

export async function listModels() {
  const res = await fetch(`${apiV2Url}admin/bailey/models`, {
    method: 'GET',
    headers: authHeader()
  });
  const data = await handleJson(res);
  return data.data || [];
}

export const baileyService = {
  streamChat,
  listSessions,
  getSessionTranscript,
  listModels
};
